import React from 'react';
import { Typography, Flex, Button, theme } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { LineChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { clampDateRange } from '@prerender/design-system/DateRangePicker';

import { CustomTooltip } from '../../components/Tooltip';
import { useChartDescriptionsState } from '~components/Charts';
import Filters, { getDefaultDateRange, timeFrameForAnalytics } from '~components/Filters';
import { PrintableExport } from '~components/PrintableExport';
import {
  ALL_CRAWLERS,
  type CrawlerCategoryScheme,
  type CrawlerFilterSearchParams,
  fromSearchParams,
  toCategories,
  toCrawlerSelectedEvent,
  toSearchParams,
} from '~features/crawler-filter';
import {
  type CrawlerFilterUpdate,
  useCrawlerCategoryScheme,
  useCrawlerSelection,
} from '~features/crawler-filter/CrawlerFilterHooks';
import { CRAWLERS_LIST_ENDPOINT, useGetCrawlersFiltersListQuery } from '~features/crawler-filter/CrawlerFilterApi';
import useQueryStringParameters from '../../utils/useQueryStringParameters';
import {
  CRAWLED_PAGES_ENDPOINT,
  useGetCrawlerRequestsQuery,
  useGetResponseTimeQuery,
  useGetVolumeQuery,
  useExportCrawlerRequestsMutation,
} from './CDNAnalyticsAPI';
import { CrawlerTrafficEmptyState, EmptyRangeState, NoCrawlersSelected, queryErrorDetail } from '~features/empty-states';
import { useCrawlerTrafficEmptyScope, widenedDateWindow } from '~features/empty-states/scope';
import {
  buildCDNAnalyticsQueryParameters,
  type CDNAnalyticQueryParams,
  type CDNAnalyticsQueryStrings,
  QueryParamNames,
} from './QueryParameters';
import CDNAnalyticsCardWithActions from './CDNAnalyticsCardWithActions';
import CDNAnalyticsTable, { PAGE_SIZE_DEFAULT } from './CDNAnalyticsTable';
import CDNAnalyticsExportModal from './CDNAnalyticsExportModal';
import CDNAnalyticsChartsLayout from './CDNAnalyticsChartsLayout';
import CDNAnalyticsResponseTimeChart, { RESPONSE_TIME_VISIBILITY_CHARTS } from './ResponseTimeChart';
import CDNAnalyticsVolumeChart, { VOLUME_VISIBILITY_CHARTS } from './VolumeChart';
import useTrackCDNAnalyticsEvent from './useTrackCDNAnalyticsEvent';
import { useCurrentPlan, useHasTrackingCode } from '../../hooks/usePrerenderUser';
import PrintableCDNAnalytics from './PrintableCDNAnalytics';
import { appendHiddenChartItems } from '~library/chartjs';
import { IsPrintingProvider } from '~library/print';

const NON_TABLE_FILTERS = [
  QueryParamNames.DateFrom,
  QueryParamNames.DateTo,
  QueryParamNames.Crawlers,
  QueryParamNames.Domain,
] as const;

const NON_TRACKABLE_FILTERS = [...NON_TABLE_FILTERS, QueryParamNames.Page, QueryParamNames.PageSize] as const;
const TABLE_FILTERS = [
  QueryParamNames.Query,
  QueryParamNames.AdaptiveType,
  QueryParamNames.CacheHit,
  QueryParamNames.StatusCode,
  QueryParamNames.StatusCodeLow,
  QueryParamNames.StatusCodeHigh,
  QueryParamNames.Timedout,
  QueryParamNames.ResponseTimeHigh,
  QueryParamNames.ResponseTimeLow,
] as const;
const TABLE_VIEW_PARAMETERS = [
  QueryParamNames.QueryCondition,
  QueryParamNames.Sort,
  QueryParamNames.SortDirection,
  QueryParamNames.Page,
  QueryParamNames.PageSize,
  QueryParamNames.ChartType,
] as const;
type TableResetParameter = typeof TABLE_FILTERS[number] | typeof TABLE_VIEW_PARAMETERS[number];
const TABLE_FILTERS_RESET = Object.fromEntries(
  [...TABLE_FILTERS, ...TABLE_VIEW_PARAMETERS].map((parameter) => [parameter, undefined])
) as Record<TableResetParameter, undefined>;
const UNTRACKED_PARAMETERS = ['categoryScheme'];
const CDN_ANALYTICS_PRINT_CHARTS = [...VOLUME_VISIBILITY_CHARTS, ...RESPONSE_TIME_VISIBILITY_CHARTS];
const CDN_ANALYTICS_PRINT_CHART_IDS = CDN_ANALYTICS_PRINT_CHARTS.map((chart) => chart.id);

type CrawlerRequestsQuery = Partial<CDNAnalyticQueryParams> & { categoryScheme: CrawlerCategoryScheme };

const removeParamsIfNeeded = (params: CrawlerRequestsQuery): CrawlerRequestsQuery => {
  if (params.q_condition && !params.q) {
    delete params.q_condition;
  }

  if (params.chart_type) {
    delete params.chart_type;
  }

  return params;
};

// A trustworthy `page`/`page_size` URL param addresses a real page — a positive
// integer. Anything else (negative, zero, non-numeric → NaN) is "junk".
function isPositivePageParam(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value > 0;
}

function CDNAnalytics({ print }: { print?: boolean }) {
  const isPrinting = Boolean(print);
  const { token } = theme.useToken();
  const trackEvent = useTrackCDNAnalyticsEvent();
  const { hash, search: locationSearch } = useLocation();
  const currentPlan = useCurrentPlan();
  const notIntegrated = !useHasTrackingCode();
  const tableRef = React.useRef<HTMLDivElement>(null);
  const [queryStringsAdjusted, setQueryStringsAdjusted] = React.useState<boolean>(false);
  const [chartDescriptionsEnabled, onChartDescriptionsEnabledChange] = useChartDescriptionsState();

  const { dateFrom: DEFAULT_DATE_FROM, dateTo: DEFAULT_DATE_TO } = getDefaultDateRange();

  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [ExportCrawlerRequests, ExportCrawlerRequestsResult] = useExportCrawlerRequestsMutation();
  const { queryStrings, updateMultipleParameters } = useQueryStringParameters<
    CDNAnalyticsQueryStrings,
    CDNAnalyticsQueryStrings & Partial<CrawlerFilterSearchParams>
  >(buildCDNAnalyticsQueryParameters);
  const categoryScheme = useCrawlerCategoryScheme();
  const crawlerFilter = React.useMemo(
    () => fromSearchParams(new URLSearchParams(locationSearch), categoryScheme),
    [locationSearch, categoryScheme]
  );

  // Untrusted `page`/`page_size` URL params (stale links, hand-edits). Junk
  // params fall back to the first page / default size for the fetch; the effect
  // below then rewrites the URL to match where the user actually landed.
  const pageParamIsJunk = queryStrings.page !== undefined && !isPositivePageParam(queryStrings.page);
  const pageSizeParamIsJunk = queryStrings.page_size !== undefined && !isPositivePageParam(queryStrings.page_size);
  const effectivePageSize = isPositivePageParam(queryStrings.page_size) ? queryStrings.page_size : PAGE_SIZE_DEFAULT;
  const requestedPage = isPositivePageParam(queryStrings.page) ? queryStrings.page : 1;

  // MULTIPLE REQUESTS FOR EXACT ERROR MESSAGES
  const AllCrawlersResult = useGetCrawlersFiltersListQuery({ categoryScheme }, { skip: notIntegrated });

  const { crawlerUniverse, effectiveCrawlerFilter, pageCrawlerFilter, noCrawlersInScope, noteFiltersUpdate } =
    useCrawlerSelection({ urlFilter: crawlerFilter, everCrawled: AllCrawlersResult, categoryScheme, isPrinting });
  const updateParameters = React.useCallback<typeof updateMultipleParameters>(
    (parameters, options) => {
      noteFiltersUpdate({});
      updateMultipleParameters(parameters, options);
    },
    [noteFiltersUpdate, updateMultipleParameters]
  );
  const selectedCrawlerCategories = React.useMemo(
    () => toCategories(effectiveCrawlerFilter, categoryScheme),
    [effectiveCrawlerFilter, categoryScheme]
  );

  const crawlerRequestsQuery = (): CrawlerRequestsQuery => {
    const output: CrawlerRequestsQuery = {
      ...queryStrings,
      crawlers: selectedCrawlerCategories,
      categoryScheme,
    };

    if (!output[QueryParamNames.DateFrom] || !output[QueryParamNames.DateTo]) {
      output[QueryParamNames.DateFrom] = DEFAULT_DATE_FROM;
      output[QueryParamNames.DateTo] = DEFAULT_DATE_TO;
    }

    // Never fetch with junk pagination — fall back to the first page / default
    // size. A valid-but-too-large page is kept so the response reveals the real
    // total, which the correction effect then uses to land on the last page.
    output.page = requestedPage;
    output.page_size = effectivePageSize;

    return output;
  };

  const getCrawlerVisitsParams = () => {
    return {
      dateFrom: queryStrings.dateFrom || DEFAULT_DATE_FROM,
      dateTo: queryStrings.dateTo || DEFAULT_DATE_TO,
      crawlers: selectedCrawlerCategories,
      domain: queryStrings.domain,
      categoryScheme,
    };
  };

  // Not "always wait for the list": that would delay every visit, and only a link listing crawlers can
  // change (collapse to "all") once the list arrives.
  const crawlerListSettled = AllCrawlersResult.isSuccess || AllCrawlersResult.isError;
  const requestsReady =
    !notIntegrated && queryStringsAdjusted && (crawlerFilter.kind === 'all' || crawlerListSettled) && !noCrawlersInScope;
  const requestParameters = {
    skip: !requestsReady,
    refetchOnMountOrArgChange: true,
  };
  const PagesCrawledResult = useGetCrawlerRequestsQuery(
    removeParamsIfNeeded(crawlerRequestsQuery()),
    requestParameters
  );
  const ResponseTimeResult = useGetResponseTimeQuery(getCrawlerVisitsParams(), requestParameters);
  const VolumeResult = useGetVolumeQuery(getCrawlerVisitsParams(), requestParameters);

  const crawledPages = PagesCrawledResult.isError ? undefined : PagesCrawledResult.data;
  const totalCount = crawledPages?.totalCount ?? 0;
  const lastPage = totalCount > 0 ? Math.ceil(totalCount / effectivePageSize) : 1;
  // A valid page number past the end (e.g. a bookmarked link after the data set
  // shrank) — the nearest real page is the last one, not the first.
  const pageExceedsData =
    !pageParamIsJunk &&
    !PagesCrawledResult.isFetching &&
    crawledPages !== undefined &&
    totalCount > 0 &&
    requestedPage > lastPage;
  // Keep the table spinner up while correcting so the "No crawl requests found"
  // message never flashes while real rows exist on another page.
  const isCorrectingPagination = queryStringsAdjusted && (pageParamIsJunk || pageSizeParamIsJunk || pageExceedsData);
  const chartsFetching = ResponseTimeResult.isFetching || VolumeResult.isFetching;
  const isEverythingLoaded = requestsReady && !chartsFetching;

  /** Matches each chart: `!!rawData && !!rawData.data.length` (volume + response time). */
  const volumeEmpty = !VolumeResult.data?.data.length;
  const responseTimeEmpty = !ResponseTimeResult.data?.data.length;
  const hasDataForTimeRange = !volumeEmpty && !responseTimeEmpty;

  const dateFrom = queryStrings[QueryParamNames.DateFrom] || DEFAULT_DATE_FROM;
  const dateTo = queryStrings[QueryParamNames.DateTo] || DEFAULT_DATE_TO;

  const pageResults = [PagesCrawledResult, VolumeResult, ResponseTimeResult];
  const everyRequestFailed = pageResults.every((result) => result.isError);
  const retryEverything = () => pageResults.forEach((result) => result.refetch());
  const everythingLoadedEmpty =
    requestsReady &&
    pageResults.every((result) => result.isSuccess) &&
    !chartsFetching &&
    !PagesCrawledResult.isFetching &&
    volumeEmpty &&
    responseTimeEmpty &&
    totalCount === 0;
  const tableFiltersActive = TABLE_FILTERS.some((key) => queryStrings[key] !== undefined);
  const emptyScope = useCrawlerTrafficEmptyScope({
    emptyInRange: everythingLoadedEmpty,
    crawlerList: AllCrawlersResult,
    allDomains: !queryStrings.domain,
    crawlerFilter: effectiveCrawlerFilter,
    selectedCrawlerCategories,
    dateFrom,
    dateTo,
    isPrinting,
  });
  const crawlerListPending = !crawlerListSettled;
  const settlingEmptyRange = emptyScope.counting || (everythingLoadedEmpty && crawlerListPending);
  const tableSettled = !PagesCrawledResult.isFetching && (PagesCrawledResult.isSuccess || PagesCrawledResult.isError);
  const pageStateUndecided = !(requestsReady && tableSettled && crawlerListSettled && !emptyScope.counting);
  const emptyVolumeAwaitsPageState = volumeEmpty && !VolumeResult.isError && pageStateUndecided;
  const emptyResponseTimeAwaitsPageState = responseTimeEmpty && !ResponseTimeResult.isError && pageStateUndecided;
  const emptyTableAwaitsPageState = totalCount === 0 && !PagesCrawledResult.isError && pageStateUndecided;
  const emptyRangeUnknowable = everythingLoadedEmpty && AllCrawlersResult.isError;
  const pageLoadError = everyRequestFailed
    ? { detail: queryErrorDetail(PagesCrawledResult.error, CRAWLED_PAGES_ENDPOINT), retry: retryEverything }
    : emptyRangeUnknowable
      ? { detail: queryErrorDetail(AllCrawlersResult.error, CRAWLERS_LIST_ENDPOINT), retry: AllCrawlersResult.refetch }
      : undefined;
  const canExportChartsPdf = isEverythingLoaded && hasDataForTimeRange && !pageLoadError;
  const exportChartsLoading = !isEverythingLoaded && !noCrawlersInScope;
  const showEmptyRangeState = everythingLoadedEmpty && !settlingEmptyRange && !emptyRangeUnknowable;
  const onDateRangeWidened = () => {
    const { dateFrom: widenedFrom, dateTo: widenedTo } = widenedDateWindow();
    updateParameters({ [QueryParamNames.DateFrom]: widenedFrom, [QueryParamNames.DateTo]: widenedTo });
  };

  const buildPropertiesToTrack = (activeParameters: Record<string, any>, keepPages = true) => {
    const propertiesToTrack = Object.keys(activeParameters).reduce<Record<string, any>>((acc, key) => {
      if (UNTRACKED_PARAMETERS.includes(key)) return acc;
      if (keepPages) {
        if (NON_TABLE_FILTERS.includes(key as typeof NON_TABLE_FILTERS[number]) || !activeParameters[key]) return acc;
      } else {
        if (NON_TRACKABLE_FILTERS.includes(key as typeof NON_TRACKABLE_FILTERS[number]) || !activeParameters[key])
          return acc;
      }

      acc[key] = activeParameters[key];
      return acc;
    }, {} as Record<string, any>);

    propertiesToTrack.selected_crawlers = toCrawlerSelectedEvent(
      effectiveCrawlerFilter,
      crawlerUniverse,
      categoryScheme
    ).selected_crawlers;

    propertiesToTrack.selected_domain = activeParameters.domain || ['All domains'];

    propertiesToTrack.time_frame = timeFrameForAnalytics(dayjs(activeParameters.dateFrom), dayjs(activeParameters.dateTo));
    if (activeParameters.dateFrom) propertiesToTrack.date_from = dayjs(activeParameters.dateFrom).format('YYYY-MM-DD');
    if (activeParameters.dateTo) propertiesToTrack.date_to = dayjs(activeParameters.dateTo).format('YYYY-MM-DD');

    return propertiesToTrack;
  };

  const onFiltersUpdated = ({
    crawlerFilter: newCrawlerFilter,
    ...newFilters
  }: CDNAnalyticsQueryStrings & CrawlerFilterUpdate) => {
    const propertiesToTrack: Record<string, any> = {};
    const newFilterKeys = Object.keys(newFilters) as (keyof CDNAnalyticsQueryStrings)[];
    newFilterKeys.forEach((key) => {
      if (NON_TRACKABLE_FILTERS.includes(key as typeof NON_TRACKABLE_FILTERS[number]) || !newFilters[key]) return;

      propertiesToTrack[key] = newFilters[key];
    });

    if (Object.keys(propertiesToTrack).length) {
      trackEvent('TableFiltersChanged', propertiesToTrack);
    }
    noteFiltersUpdate({ crawlerFilter: newCrawlerFilter });
    updateMultipleParameters({ ...newFilters, ...(newCrawlerFilter ? toSearchParams(newCrawlerFilter) : {}) });
  };

  const onPaginationUpdated = React.useCallback(
    (page: CDNAnalyticQueryParams['page'], page_size: CDNAnalyticQueryParams['page_size'] = PAGE_SIZE_DEFAULT) => {
      updateParameters({ page, page_size }, { filterNullish: true, resetPagination: false });
    },
    [updateParameters]
  );

  React.useEffect(() => {
    if (!queryStringsAdjusted) return;
    if (pageParamIsJunk || pageSizeParamIsJunk) {
      // Junk params can't address a real page — drop the user on the first page.
      onPaginationUpdated(1, effectivePageSize);
    } else if (pageExceedsData) {
      // Too-large but real number — drop the user on the nearest real page.
      onPaginationUpdated(lastPage, effectivePageSize);
    }
  }, [queryStringsAdjusted, pageParamIsJunk, pageSizeParamIsJunk, pageExceedsData, lastPage, effectivePageSize, onPaginationUpdated]);

  const onFiltersCleared = () => {
    trackEvent('TableFiltersCleared', {});
    updateParameters({ ...TABLE_FILTERS_RESET });
  };

  const onAllFiltersCleared = () => {
    noteFiltersUpdate({ crawlerFilter: ALL_CRAWLERS });
    updateMultipleParameters({
      ...toSearchParams(ALL_CRAWLERS),
      [QueryParamNames.Domain]: undefined,
      [QueryParamNames.DateFrom]: DEFAULT_DATE_FROM,
      [QueryParamNames.DateTo]: DEFAULT_DATE_TO,
      ...TABLE_FILTERS_RESET,
    });
  };

  const onExportModalClose = () => {
    setExportModalOpen(false);
    ExportCrawlerRequestsResult.reset();
  };

  const onExportCSV = () => {
    const appliedParameters = removeParamsIfNeeded(crawlerRequestsQuery());
    const propertiesToTrack = buildPropertiesToTrack(appliedParameters, false);
    trackEvent('ExportRequested', propertiesToTrack);
    ExportCrawlerRequests(appliedParameters);
  };

  React.useEffect(() => {
    if (queryStrings.dateFrom && queryStrings.dateTo) {
      const [adjustedDateFrom, adjustedDateTo] = clampDateRange(
        queryStrings.dateFrom,
        queryStrings.dateTo,
        currentPlan.dataRetentionDays
      );
      const datesChanged =
        adjustedDateFrom.getTime() !== queryStrings.dateFrom.getTime() ||
        adjustedDateTo.getTime() !== queryStrings.dateTo.getTime();
      if (datesChanged) {
        updateParameters({ dateFrom: adjustedDateFrom, dateTo: adjustedDateTo });
      }
    }
    setQueryStringsAdjusted(true);
  }, [queryStrings.dateFrom, queryStrings.dateTo, currentPlan.dataRetentionDays]);

  React.useEffect(() => {
    if (hash.includes('table') && PagesCrawledResult.isSuccess) {
      tableRef.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
    }
  }, [hash, PagesCrawledResult.isSuccess]);

  React.useEffect(() => {
    if (!isPrinting) {
      const activeParameters = crawlerRequestsQuery();
      const propertiesToTrack = buildPropertiesToTrack(activeParameters);
      trackEvent('PageViewed', { ...propertiesToTrack, version: 'v2' });
    }
  }, [isPrinting]);

  const chartNoDataFallback = (
    <EmptyRangeState
      screen="cdn_analytics"
      scope={emptyScope.state}
      onClearFilters={onAllFiltersCleared}
      onWidenDateRange={onDateRangeWidened}
      compact
    />
  );

  if (isPrinting) {
    return (
      <PrintableExport
        subject="CDN Analytics"
        ready={isEverythingLoaded || notIntegrated}
        chartIds={CDN_ANALYTICS_PRINT_CHART_IDS}
      >
        <PrintableCDNAnalytics
          filters={{
            domain: queryStrings.domain,
            crawlers: selectedCrawlerCategories,
            dateFrom: queryStrings[QueryParamNames.DateFrom] || DEFAULT_DATE_FROM,
            dateTo: queryStrings[QueryParamNames.DateTo] || DEFAULT_DATE_TO,
          }}
          charts={{
            volume: {
              rawData: VolumeResult.data,
              isLoading: VolumeResult.isFetching,
              isError: VolumeResult.isError,
              onRetry: VolumeResult.refetch,
            },
            responseTime: {
              rawData: ResponseTimeResult.data,
              isLoading: ResponseTimeResult.isFetching,
              isError: ResponseTimeResult.isError,
              onRetry: ResponseTimeResult.refetch,
            },
          }}
          noDataFallback={
            notIntegrated ? (
              <CrawlerTrafficEmptyState screen="cdn_analytics" kind="not-integrated" compact />
            ) : (
              chartNoDataFallback
            )
          }
        />
      </PrintableExport>
    );
  }

  return (
    <IsPrintingProvider initialValue={false}>
      <section style={{ marginBottom: token.margin }}>
        <Typography.Title level={2} style={{ marginBottom: 0 }}>
          CDN Analytics
        </Typography.Title>
        <Typography.Text style={{ color: token.colorTextQuaternary }}>
          Recent crawler requests handled by our CDN.
        </Typography.Text>
      </section>

      {!notIntegrated && (
        <Flex align="flex-start" justify="space-between" gap={token.marginXS} wrap>
          <Filters
            trackEvent={trackEvent}
            updateFilters={onFiltersUpdated}
            domainSelected={queryStrings[QueryParamNames.Domain]}
            crawlerFilter={pageCrawlerFilter}
            dateFrom={queryStrings[QueryParamNames.DateFrom] || DEFAULT_DATE_FROM}
            dateTo={queryStrings[QueryParamNames.DateTo] || DEFAULT_DATE_TO}
            export={{
              label: 'Export Charts to PDF',
              url: '/export-cdn-analytics',
              search: () => appendHiddenChartItems(locationSearch, CDN_ANALYTICS_PRINT_CHARTS),
              disabled: !canExportChartsPdf,
              loading: exportChartsLoading,
            }}
            chartDescriptions={{
              enabled: chartDescriptionsEnabled,
              onChange: onChartDescriptionsEnabledChange,
            }}
          />

          <Link to="/insight" key="insight">
            <CustomTooltip message="Get insight into longterm CDN deliveries for your account" placement="left">
              <Button
                key="insight-btn"
                size="middle"
                icon={<LineChartOutlined />}
                style={{ marginBottom: token.margin }}
              >
                Explore Deep Insight
              </Button>
            </CustomTooltip>
          </Link>
        </Flex>
      )}

      <CDNAnalyticsExportModal
        open={exportModalOpen}
        onClose={onExportModalClose}
        onOk={onExportCSV}
        isLoading={ExportCrawlerRequestsResult.isLoading}
        isError={ExportCrawlerRequestsResult.isError}
        status={ExportCrawlerRequestsResult.data?.status}
      />

      {notIntegrated ? (
        <CrawlerTrafficEmptyState screen="cdn_analytics" kind="not-integrated" />
      ) : noCrawlersInScope ? (
        <NoCrawlersSelected
          screen="cdn_analytics"
          onSelectAllCrawlers={() => onFiltersUpdated({ crawlerFilter: ALL_CRAWLERS })}
        />
      ) : pageLoadError ? (
        <CrawlerTrafficEmptyState
          screen="cdn_analytics"
          kind="error"
          detail={pageLoadError.detail}
          onRetry={pageLoadError.retry}
        />
      ) : showEmptyRangeState ? (
        <EmptyRangeState
          screen="cdn_analytics"
          scope={emptyScope.state}
          onClearFilters={onAllFiltersCleared}
          onWidenDateRange={onDateRangeWidened}
        />
      ) : (
        <>
          <div style={{ marginBottom: token.margin }}>
            <CDNAnalyticsChartsLayout
              initialChartType={queryStrings.chart_type}
              descriptionVisible={chartDescriptionsEnabled}
              VolumeChart={
                <CDNAnalyticsVolumeChart
                  rawData={VolumeResult.data}
                  isLoading={VolumeResult.isFetching || emptyVolumeAwaitsPageState}
                  isError={VolumeResult.isError}
                  onRetry={VolumeResult.refetch}
                  noDataFallback={chartNoDataFallback}
                />
              }
              ResponseTimeChart={
                <CDNAnalyticsResponseTimeChart
                  rawData={ResponseTimeResult.data}
                  isLoading={ResponseTimeResult.isFetching || emptyResponseTimeAwaitsPageState}
                  isError={ResponseTimeResult.isError}
                  onRetry={ResponseTimeResult.refetch}
                  noDataFallback={chartNoDataFallback}
                />
              }
            />
          </div>

          <div ref={tableRef}>
            <CDNAnalyticsCardWithActions
              updateFilters={onFiltersUpdated}
              clearFilters={onFiltersCleared}
              exportCSV={() => setExportModalOpen(true)}
              refreshData={PagesCrawledResult.refetch}
              query={queryStrings.q}
              queryCondition={queryStrings.q_condition}
            >
              <CDNAnalyticsTable
                updateFilters={onFiltersUpdated}
                updatePagination={onPaginationUpdated}
                queryParameters={{ ...queryStrings, page: requestedPage, page_size: effectivePageSize }}
                data={crawledPages?.data}
                totalCount={totalCount}
                isLoading={PagesCrawledResult.isFetching || isCorrectingPagination || emptyTableAwaitsPageState}
                isError={PagesCrawledResult.isError}
                onRetry={PagesCrawledResult.refetch}
                emptyRows={
                  tableFiltersActive ? (
                    <CrawlerTrafficEmptyState
                      screen="cdn_analytics"
                      kind="filtered"
                      onClearFilters={onFiltersCleared}
                      compact
                    />
                  ) : (
                    chartNoDataFallback
                  )
                }
              />
            </CDNAnalyticsCardWithActions>
          </div>
        </>
      )}
    </IsPrintingProvider>
  );
}

export default CDNAnalytics;
